import type { rilascio as it } from "../it/rilascio.ts";
import { formati } from "../formati.ts";

const f = formati("en");

type Servizi = { servizi: readonly string[] };

/** Il verdetto sul rilascio senza interruzione e le fasi di un'operazione. */
export const rilascio: typeof it = {
  "rilascio.requisito.strategia.titolo": "The stack uses the blue/green strategy",
  "rilascio.requisito.strategia.come": "Change it here: new stacks start with recreate.",
  "rilascio.requisito.proxy.titolo": "Portman can reach Nginx Proxy Manager",
  "rilascio.requisito.proxy.assente":
    "An administrator can install it from here with the recommended settings, or connect " +
    "an existing one with PORTMAN_NPM_URL, PORTMAN_NPM_IDENTITY and PORTMAN_NPM_SECRET, which requires a restart.",
  "rilascio.requisito.proxy.ok": "It responds at the configured address.",
  "rilascio.requisito.proxy.muto": "The configured proxy didn't respond.",
  "rilascio.requisito.instradamento.titolo": "At least one service has a domain",
  "rilascio.requisito.instradamento.come": "Add one in Deploy › Routes: it's the domain traffic moves to.",
  "rilascio.requisito.porte.titolo": "No service publishes host ports",
  "rilascio.requisito.porte.ok":
    "Under blue/green a service's address is its domain, and other services reach it by name.",
  "rilascio.requisito.porte.mancante": (p: Servizi) =>
    `Two generations running together can't bind the same port. Remove "ports:" from ${f.elenco(p.servizi)}: ` +
    "their address is the domain, and other services reach them by name.",
  "rilascio.requisito.nomi.titolo": "No service sets a fixed container name",
  "rilascio.requisito.nomi.ok": "The project names the containers, so two generations coexist without clashing.",
  "rilascio.requisito.nomi.mancante": (p: Servizi) =>
    `A name set with "container_name:" is global on the Docker daemon, so two generations can't ` +
    `share it: the second wouldn't start. Remove it from ${f.elenco(p.servizi)} — ` +
    "under blue/green the project names the containers, and the proxy still reaches them.",
  "rilascio.requisito.fissi-dominio.titolo": "No pinned service has a domain",
  "rilascio.requisito.fissi-dominio.ok": "Traffic moves between generations: a pinned service has none.",
  "rilascio.requisito.fissi-dominio.mancante": (p: Servizi) =>
    `Traffic would have nowhere to move. Remove the domain from ${f.elenco(p.servizi)}, ` +
    "or remove x-portman.fisso if it needs a route.",
  "rilascio.requisito.fissi-dipendenze.titolo": "No pinned service depends on an unpinned one",
  "rilascio.requisito.fissi-dipendenze.ok":
    "Pinned services start before the generation, and --no-deps would ignore the dependency.",
  "rilascio.requisito.fissi-dipendenze.mancante": (p: Servizi) =>
    `${f.elenco(p.servizi)} ${p.servizi.length === 1 ? "starts" : "start"} before the generation: ` +
    `${p.servizi.length === 1 ? "it" : "they"} can't depend on a service that doesn't exist yet. ` +
    "Remove the dependency, or pin the service it depends on too.",
  "rilascio.requisito.reti-nome.titolo": "No app uses a network with a fixed name",
  "rilascio.requisito.reti-nome.ok":
    "A non-external network with name: would join both generations on the same DNS.",
  "rilascio.requisito.reti-nome.mancante": (p: Servizi) =>
    `The network with name: puts ${f.elenco(p.servizi)} on the same network in both generations: ` +
    "Docker's DNS would alternate between old and new. Remove name: or declare the network external.",
  "rilascio.requisito.fissi-dati.titolo": "No pinned service still has its data in a generation",
  "rilascio.requisito.fissi-dati.ok":
    "A newly pinned service would start with an empty volume, away from its data.",
  "rilascio.requisito.fissi-dati.mancante": (p: Servizi & { progetto: string }) =>
    `${f.elenco(p.servizi)} ${p.servizi.length === 1 ? "is" : "are"} pinned, but the data still lives in "${p.progetto}": ` +
    "the pinned service would start with an empty volume. Stop the service in the generation and copy the volume " +
    "as the guide describes (docs/PROVA-LOCALE.md, “A service that becomes pinned”), or remove x-portman.fisso.",

  "rilascio.mancanza.strategia": "the stack doesn't use blue/green",
  "rilascio.mancanza.proxy": "no connection to Nginx Proxy Manager",
  "rilascio.mancanza.instradamento": "no service has a domain",
  "rilascio.mancanza.porte": "host ports are published",
  "rilascio.mancanza.nomi": "container names are fixed",
  "rilascio.mancanza.fissi-dominio": "a pinned service has a domain",
  "rilascio.mancanza.fissi-dipendenze": "a pinned service depends on an unpinned one",
  "rilascio.mancanza.reti-nome": "a network with a fixed name would join the generations",
  "rilascio.mancanza.fissi-dati": "a pinned service still has its data in a generation",

  "rilascio.verdetto.attivo": "Active for this stack",
  "rilascio.verdetto.unaMancante": (p: { cosa: string }) => `Not active: ${p.cosa}`,
  "rilascio.verdetto.mancano": (p: { n: number; totale: number }) =>
    `Not active: ${f.numero(p.n)} of ${f.numero(p.totale)} ${f.plurale(p.totale, { one: "condition", other: "conditions" })} not met`,
  "rilascio.verdetto.nonConfigurabile": "Not active: zero-downtime deploy can't be configured",
  "rilascio.effetto.senzaInterruzione":
    "On the next start the new version runs alongside the active one, and traffic moves only once it's ready: no downtime.",
  "rilascio.effetto.ricreazione":
    "On the next start the containers will be recreated: the service is unreachable for about a second.",

  "rilascio.avvertimento.healthcheck.titolo": (p: Servizi) =>
    `${f.elenco(p.servizi)} ${p.servizi.length === 1 ? "doesn't" : "don't"} declare a healthcheck in the compose`,
  "rilascio.avvertimento.healthcheck.dettaglio":
    "If the image doesn't declare one either, traffic moves as soon as the process starts, " +
    "which doesn't mean it's ready to serve. The deploy logs will say, based on the actual container.",
  "rilascio.avvertimento.volumi.titolo": (p: Servizi) =>
    `${f.elenco(p.servizi)} ${p.servizi.length === 1 ? "mounts" : "mount"} a volume that changes with every generation`,
  "rilascio.avvertimento.volumi.dettaglio":
    "Docker creates a different volume for each generation. If it must survive deploys, pin the service; if it's a cache, that's fine.",
  "rilascio.avvertimento.ex-fissi.titolo": (p: Servizi) =>
    `${f.elenco(p.servizi)} ${p.servizi.length === 1 ? "is" : "are"} no longer pinned`,
  "rilascio.avvertimento.ex-fissi.dettaglio":
    "On deploy the container in the pinned project is removed, but its volume stays where it is: " +
    "the new generation starts with an empty volume.",

  "rilascio.fase.verifica": "Checking the configuration",
  "rilascio.fase.immagini": "Pulling images",
  "rilascio.fase.avvio": "Starting services",
  "rilascio.fase.avvioGenerazione": "Starting the new generation",
  "rilascio.fase.salute": "Waiting for the new generation to be ready",
  "rilascio.fase.traffico": "Moving traffic",
  "rilascio.fase.rimozione": "Removing the previous version",
};
